import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Round-4 hardening: we no longer store the JWT in localStorage. The backend
// sets an HttpOnly `mp_session` cookie on login and we rely on it for every
// request (`withCredentials: true` in axios). localStorage now only holds
// a boolean "has_session" hint so we can gate the initial /auth/me call —
// this avoids a 401 flash for logged-out visitors landing on / without
// having to poll the backend on every fresh tab.
const SESSION_HINT_KEY = "mypatria.hasSession";
// Idle timeout: 30 minutes total; warn 2 minutes before.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_LEAD_MS = 2 * 60 * 1000;

function readHint() {
  try { return localStorage.getItem(SESSION_HINT_KEY) === "1"; } catch { return false; }
}
function writeHint(v) {
  try {
    if (v) localStorage.setItem(SESSION_HINT_KEY, "1");
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch { /* storage may be blocked in private mode — safe to ignore */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const lastActivity = useRef(Date.now());
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);

  const logout = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) {
      try { await api.post("/auth/logout"); }
      catch (err) { console.error("logout call failed", err); }
    } else {
      // On silent (idle-timeout) logout we still want the cookie cleared.
      try { await api.post("/auth/logout"); } catch { /* ignore — cookie may already be gone */ }
    }
    writeHint(false);
    setUser(null);
    setShowIdleWarning(false);
  }, []);

  const scheduleIdle = useCallback(() => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    warningTimer.current = setTimeout(() => setShowIdleWarning(true), IDLE_TIMEOUT_MS - WARNING_LEAD_MS);
    logoutTimer.current = setTimeout(() => logout({ silent: true }), IDLE_TIMEOUT_MS);
  }, [logout]);

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.post("/auth/refresh");
      setUser(data.user);
      writeHint(true);
      setShowIdleWarning(false);
      lastActivity.current = Date.now();
      scheduleIdle();
      return true;
    } catch (err) {
      console.error("session refresh failed", err);
      await logout({ silent: true });
      return false;
    }
  }, [logout, scheduleIdle]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Cookie is now set server-side. We keep the user object in state and
    // set a lightweight hint so subsequent tabs can immediately try /auth/me.
    setUser(data.user);
    writeHint(true);
    lastActivity.current = Date.now();
    scheduleIdle();
    return data.user;
  }, [scheduleIdle]);

  // Bootstrap: hint OR fresh cookie both trigger /auth/me. If /me 401s we
  // silently drop the hint and land on /login.
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      if (!readHint()) { setLoading(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        setUser(data);
        scheduleIdle();
      } catch {
        writeHint(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    bootstrap();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track activity for idle-timeout.
  useEffect(() => {
    if (!user) return undefined;
    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    let throttle;
    const onActivity = () => {
      if (throttle) return;
      throttle = setTimeout(() => { throttle = null; }, 5000);
      lastActivity.current = Date.now();
      if (!showIdleWarning) scheduleIdle();
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (throttle) clearTimeout(throttle);
    };
  }, [user, scheduleIdle, showIdleWarning]);

  useEffect(() => () => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
  }, []);

  // 401 interceptor — drop to login on any expired-session response.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401 && user) {
          logout({ silent: true });
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
  }, [user, logout]);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshSession,
    setUser,
    showIdleWarning,
    dismissIdleWarning: () => setShowIdleWarning(false),
    backendUrl: process.env.REACT_APP_BACKEND_URL,
    // `token` is now null for the browser path — the JWT lives in an HttpOnly
    // cookie and JS deliberately can't reach it. Consumers that historically
    // read `token` should treat null as "signed in via cookie".
    token: null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
